<template>
  <!-- ----------------- -->
  <!-- TOP ROW (HEADER)  -->
  <!-- ----------------- -->
  <div class="row">
    <confirm-dialog ref="confirmDialog"></confirm-dialog>
    <!-- ------------------------------------------------------------>
    <!-- ROW 1 - MAIN CONTAINER -->
    <!-- ---------------------- -->
    <div
      v-if="isReady"
      class="row mt-1 mb-3 combat-main-row"
    >
      <div class="row mt-3"></div>
      <div class="row mt-3"></div>
      <!-- ------------------------------------------------------------>
      <!-- COL 1: FAV + SUMMARY  -->
      <!-- --------------------- -->
      <div class="col combat-left-column">
        <!-- ------------------------------------------------------------>
        <!-- ROW 2 -->
        <!-- ----- -->
        <div class="row">
          <div
            class="bb-hdg ms-3 mb-3"
            style="color: #25bfda"
          >
            <span
              class="d-inline-block favourites-tooltip-trigger"
              v-tooltip:right="favouritesTooltipText"
              tabindex="0"
            >
              Favourites
              <span
                class="bb-orange-circle"
                style="margin-left: 0.1em"
              ></span>
            </span>
          </div>
          <!-- ------------------------------------------------------- -->
          <!--   COL 1.1: FAVOURITE SKILLS -->
          <!-- ------------------------------>
          <div class="ps-1 col-md-8">
            <SheetFavourites
              :character="local_character || character"
              :favs="filterFavSkills"
            />
            <div class="row mt-3"></div>
            <p
              class="bb-hdg mt-3 ms-3"
              style="color: #25bfda"
            >
              Magic Point Sources<span
                v-if="powerSourceOverflowCount > 0"
                class="power-source-overflow-message"
              >
                &nbsp;[ *** {{ powerSourceOverflowCount }}
                {{ powerSourceOverflowCount === 1 ? 'source' : 'sources' }} not
                shown ***]</span
              >
            </p>
          </div>
          <!-- ------------------------------------------------------- -->
          <!--   COL 1.2: SUMMARY   -->
          <!-- ----------------------->
          <div class="col-md-4 summary-column combat-summary-column">
            <div class="combat-summary-root">
              <div class="row gx-1 combat-summary-grid">
                <div
                  class="combat-summary-section combat-summary-section-chars"
                >
                  <SheetSummaryChars
                    :character="local_character || character"
                  />
                </div>
                <div
                  class="combat-summary-section combat-summary-section-abilities"
                >
                  <SheetSummaryAbilities
                    :character="local_character || character"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <!-- ------------------------------------------------------------>
      <!--   COL 2: HIT LOCATIONS -->
      <!-- ------------------------->
      <div class="col combat-hit-locations-column">
        <SheetHitLocations
          :character="local_character || character"
          :lock-combat-state="isNewCharacterDraft"
          @update-fatigue-override="updateFatigueOverride"
          @update-hp-cur="updateArmourLocationHpCur"
        />
      </div>
    </div>
    <!-- ------------------------------------------------------------>
    <!--   ROW 2: MAGIC POINTS  -->
    <!-- ------------------------->
    <div
      v-if="isReady"
      class="row"
    >
      <div class="ps-1 col-12">
        <SheetMagicPoints
          :character="local_character || character"
          :power_storage_items="visiblePowerStorageItems"
          :lock-character-mp="isNewCharacterDraft"
          @update-power-source="updatePowerSource"
        />
      </div>
    </div>
  </div>
</template>

<script>
import SheetSummaryAbilities from './SheetSummaryAbilities.vue'
import SheetMagicPoints from './SheetMagicPoints.vue'
import SheetHitLocations from './SheetHitLocations.vue'
import SheetSummaryChars from './SheetSummaryChars.vue'
import SheetFavourites from './SheetFavourites.vue'
import ConfirmDialog from './ConfirmDialog.vue'

import syncCharacterCombatStateFromArmourLocations from '../helpers/syncCharacterCombatStateFromArmourLocations'
import capitaliseFirstLetter from '../helpers/capitaliseFirstLetter'
import copyObjectNoRef from '../helpers/copyObjectNoRef'
import isAlphanumeric from '../helpers/isAlphanumeric'
import isNumeric from '../helpers/isNumeric'

import { inject } from 'vue'

export default {
  name: 'CharacterSheetCombat',
  emits: ['update', 'clear-error', 'sync-combat-state'],
  //========================================
  // props
  //-------
  props: {
    character: {
      type: Object,
      required: true,
    },
    listScope: {
      type: String,
      required: false,
      default: '',
    },
    errorFields: {
      type: Array,
      required: true,
    },
  },
  //========================================================
  // components
  //------------
  components: {
    ConfirmDialog,
    SheetFavourites,
    SheetSummaryAbilities,
    SheetSummaryChars,
    SheetHitLocations,
    SheetMagicPoints,
  },
  //========================================================
  // setup
  //-------
  setup() {
    const global = inject('global')
    return { global }
  },
  //========================================================
  // data
  //------
  data() {
    return {
      //
      local_character: null,
      //
      // per-field debounce timers (key -> timeout id)
      pendingTimers: {},
      combatSyncHandle: null,
      combatSyncUsesAnimationFrame: false,
    }
  },
  //========================================================
  // watch
  //-------
  watch: {
    //-----------------
    // watch character
    //-----------------
    character: {
      deep: true,
      immediate: true,
      handler(newVal) {
        if (!newVal) return
        if (newVal === this.local_character) return
        //---------------------
        // get dropdown values
        //---------------------
        // this.getDropDownValues()
        //---------------------------------------------------------------
        // load local character every time character changes from parent
        //---------------------------------------------------------------
        this.local_character = this.normalisePowerSourceState(
          copyObjectNoRef(newVal),
        )
      },
    },
  },
  //========================================================
  // computed
  //-----------
  computed: {
    favouriteSkillLimits() {
      const config =
        typeof this.$store?.getters?.['combat/getConfig'] === 'function'
          ? this.$store.getters['combat/getConfig']()
          : this.$store?.getters?.['combat/getConfig'] || {}
      const configuredLimits = config?.favourite_skill_limits || {}

      return {
        core:
          typeof configuredLimits.core === 'number' &&
          configuredLimits.core >= 0
            ? configuredLimits.core
            : 7,
        weapon:
          typeof configuredLimits.weapon === 'number' &&
          configuredLimits.weapon >= 0
            ? configuredLimits.weapon
            : 4,
        magic:
          typeof configuredLimits.magic === 'number' &&
          configuredLimits.magic >= 0
            ? configuredLimits.magic
            : 3,
      }
    },
    favouritesTooltipText() {
      return [
        'Favourite skills (up to):',
        `Core skills: ${this.favouriteSkillLimits.core}`,
        `Weapon skills: ${this.favouriteSkillLimits.weapon}`,
        `Casting skills: ${this.favouriteSkillLimits.magic}`,
      ].join('\n')
    },
    filterFavSkills() {
      if (!this.local_character) return []
      return this.local_character.skills.filter((s) => s.favourite) || []
    },
    maxVisiblePowerStorageItems() {
      return 3
    },
    allPowerStorageItems() {
      if (!this.local_character) return []
      return (
        (this.local_character.items || [])
          .filter((item) => Number(item?.pwr_storage ?? 0) > 0)
          .map(
            ({
              id,
              item,
              name,
              display_name,
              pwr_storage,
              pwr_storage_cur,
            }) => ({
              type: 'item',
              id,
              item,
              name,
              display_name,
              pwr_storage,
              pwr_storage_cur: Math.min(
                Number(pwr_storage_cur) || 0,
                Number(pwr_storage) || 0,
              ),
            }),
          ) || []
      )
    },
    visiblePowerStorageItems() {
      return this.allPowerStorageItems.slice(
        0,
        this.maxVisiblePowerStorageItems,
      )
    },
    powerSourceOverflowCount() {
      return Math.max(
        0,
        this.allPowerStorageItems.length - this.maxVisiblePowerStorageItems,
      )
    },
    isReady() {
      if (this.local_character != null) {
        return true
      }
      return false
    },
    isNewCharacterDraft() {
      if (!this.isReady) {
        return false
      }

      const rawId = this.local_character?.id
      const rawSlug = this.local_character?.slug
      const hasPersistedStringId =
        typeof rawId === 'string' && rawId.trim().length > 0
      const hasPersistedNumericId =
        typeof rawId === 'number' && Number.isFinite(rawId) && rawId > 0
      const hasPersistedSlug =
        typeof rawSlug === 'string' && rawSlug.trim().length > 0

      return !(
        hasPersistedNumericId ||
        hasPersistedStringId ||
        hasPersistedSlug
      )
    },
  },
  //========================================================
  // methods
  //---------
  methods: {
    normalisePowerSourceState(character) {
      if (!character || typeof character !== 'object') {
        return character
      }

      const nextCharacter = character
      const totalMp = Number(nextCharacter.mp) || 0
      const currentMp = Number(nextCharacter.mp_cur)
      const resolvedMpCur = Math.max(
        0,
        Math.min(totalMp, Number.isNaN(currentMp) ? totalMp : currentMp),
      )
      nextCharacter.mp_cur = resolvedMpCur

      if (Array.isArray(nextCharacter.items)) {
        nextCharacter.items = nextCharacter.items.map((item) => {
          const totalStorage = Number(item?.pwr_storage) || 0
          const currentStorage = Number(item?.pwr_storage_cur)
          const resolvedStorageCur = Math.max(
            0,
            Math.min(
              totalStorage,
              Number.isNaN(currentStorage) ? 0 : currentStorage,
            ),
          )

          return {
            ...item,
            pwr_storage_cur: resolvedStorageCur,
          }
        })
      }

      return nextCharacter
    },
    //========================================================
    // emit update
    //-------------
    emitUpdate(key, value) {
      if (this.isProfileLocked && key !== 'status') return
      console.log('emitUpdate: ' + key + ' = ' + value)
      // clear error for this field when value changes
      this.clearError(key)
      this.$emit('update', { type: 'profile', key: key, value: value })
    },
    //========================================================
    // delayed emit update
    //---------------------
    delayedEmitUpdate(key, value, delay = 600) {
      if (this.isProfileLocked && key !== 'status') return
      //-----------------------------------
      // clear existing timer for this key
      //-----------------------------------
      if (this.pendingTimers[key]) {
        clearTimeout(this.pendingTimers[key])
      }
      //---------------
      // schedule emit
      //---------------
      this.pendingTimers[key] = setTimeout(() => {
        this.emitUpdate(key, value)
        delete this.pendingTimers[key]
      }, delay)
    },
    //========================================================
    // capitalise handler
    //--------------------
    capitaliseHandler(field) {
      this.local_character[field] = capitaliseFirstLetter(
        this.local_character[field],
      )
    },
    //========================================================
    // capitalise then emit update
    //-----------------------------
    capitaliseAndDelayedEmitUpdate(field, delay = 600) {
      this.capitaliseHandler(field)
      this.delayedEmitUpdate(field, this.local_character[field], delay)
    },
    //======================================================
    // capitalise after special char
    //-------------------------------
    capitaliseAfterSpecialChar() {
      //--------------------------
      // set name as target field
      //--------------------------
      let el = document.getElementById('name')
      if (el) {
        //------------------------
        // listen to name keydown
        //------------------------
        el.addEventListener('keydown', function (e) {
          //--------------------
          // ignore command key
          //--------------------
          if (
            e.key !== 'Delete' &&
            e.key !== 'Backspace' &&
            !e.shiftKey &&
            !e.ctrlKey &&
            !e.option &&
            !e.altKey
          ) {
            let fieldVal = String(e.target.value)
            //---------------------
            // check previous char
            //---------------------
            let prevChar = fieldVal.charAt(e.target.value.length - 1)
            //------------------------------------
            // uc this char if prev = apos/hyphen
            //------------------------------------
            if (prevChar == "'" || prevChar == '-') {
              let key = e.key.toUpperCase()
              e.target.value = e.target.value + key
              e.preventDefault()
            }
          }
        })
      }
    },
    //========================================================
    // on key down
    //-------------
    onKeyDown(e) {
      if (!isAlphanumeric(e)) e.preventDefault()
    },
    //========================================================
    // on key down allow comma
    //-------------------------
    onKeyDownAllowComma(e) {
      if (e.key === ',') return
      this.onKeyDown(e)
    },
    //========================================================
    // on key down numeric
    //---------------------
    onKeyDownNumeric(e) {
      if (!isNumeric(e)) e.preventDefault()
    },
    //========================================================
    // clear error
    //-------------
    clearError(fieldName) {
      this.$emit('clear-error', fieldName)
    },
    updatePowerSource(change) {
      if (!this.local_character || !change?.key) {
        return
      }

      if (change.type === 'profile' && change.key === 'mp_cur') {
        const maxMp = Number(this.local_character.mp) || 0
        const nextValue = Math.max(
          0,
          Math.min(Number(change.value) || 0, maxMp),
        )
        this.local_character.mp_cur = nextValue
        this.$emit('update', {
          type: 'profile',
          key: 'mp_cur',
          value: nextValue,
        })
        return
      }

      if (change.type !== 'items' || !change.id) {
        return
      }

      const targetItem = Array.isArray(this.local_character.items)
        ? this.local_character.items.find(
            (item) => String(item?.id) === String(change.id),
          )
        : null

      if (!targetItem) {
        return
      }

      const maxStorage = Number(targetItem.pwr_storage) || 0
      const nextValue = Math.max(
        0,
        Math.min(Number(change.value) || 0, maxStorage),
      )
      targetItem.pwr_storage_cur = nextValue

      this.$emit('update', {
        type: 'items',
        id: targetItem.id,
        name: targetItem.display_name || targetItem.name || change.name || '',
        key: 'pwr_storage_cur',
        value: nextValue,
        object: targetItem,
      })
    },
    updateFatigueOverride(value) {
      const nextOverride = value || null
      const currentOverride = this.local_character?.fatigue_override || null

      if (nextOverride === currentOverride) {
        return
      }

      if (this.local_character) {
        this.local_character = syncCharacterCombatStateFromArmourLocations(
          this.local_character,
          {
            overrideFatigue: nextOverride,
          },
        )
      }
      this.emitUpdate('fatigue_override', nextOverride)
    },
    async showDeathDialog() {
      if (!this.$refs.confirmDialog || !this.local_character?.name) {
        return
      }

      await this.$refs.confirmDialog.show({
        title: 'Character is Dead',
        subject: 'Zero CON',
        okButton: 'OK',
      })
    },
    async updateArmourLocationHpCur(change) {
      if (!this.local_character || !change?.id) {
        return
      }

      const changeId = String(change.id)

      const index = Array.isArray(this.local_character.armour_locations)
        ? this.local_character.armour_locations.findIndex(
            (location) => String(location?.id) === changeId,
          )
        : -1

      if (index === -1) {
        return
      }

      const armourLocation = this.local_character.armour_locations[index]
      const previousHpCur = armourLocation.hp_cur

      if (previousHpCur === change.hp_cur) {
        return
      }

      armourLocation.hp_cur = change.hp_cur

      this.$emit('update', {
        type: 'armour_locations',
        id: armourLocation.id,
        name: armourLocation.armour_location_name || armourLocation.name || '',
        key: 'hp_cur',
        value: change.hp_cur,
        object: armourLocation,
      })

      this.scheduleCombatStateSync({
        hpChange: {
          previousHpCur,
          nextHpCur: change.hp_cur,
        },
      })
    },
    scheduleCombatStateSync(options = {}) {
      if (!this.local_character) {
        return
      }

      this.cancelCombatStateSync()

      const runSync = async () => {
        this.combatSyncHandle = null
        const previousStatus = this.local_character?.status

        if (!this.local_character) {
          return
        }

        this.local_character = syncCharacterCombatStateFromArmourLocations(
          this.local_character,
          options,
        )

        this.$emit('sync-combat-state', this.local_character)

        if (
          previousStatus !== 'Dead' &&
          this.local_character.status === 'Dead'
        ) {
          await this.showDeathDialog()
        }
      }

      if (typeof window !== 'undefined' && window.requestAnimationFrame) {
        this.combatSyncUsesAnimationFrame = true
        this.combatSyncHandle = window.requestAnimationFrame(() => {
          runSync()
        })
        return
      }

      this.combatSyncUsesAnimationFrame = false
      this.combatSyncHandle = setTimeout(() => {
        runSync()
      }, 0)
    },
    cancelCombatStateSync() {
      if (this.combatSyncHandle === null) {
        return
      }

      if (
        this.combatSyncUsesAnimationFrame &&
        typeof window !== 'undefined' &&
        window.cancelAnimationFrame
      ) {
        window.cancelAnimationFrame(this.combatSyncHandle)
      } else {
        clearTimeout(this.combatSyncHandle)
      }

      this.combatSyncHandle = null
    },
  },
  //========================================================
  // mounted
  //---------
  mounted() {},
  //========================================================
  // before unmount
  //----------------
  beforeUnmount() {
    // clear any outstanding timers
    Object.values(this.pendingTimers || {}).forEach((t) => clearTimeout(t))
    this.pendingTimers = {}
    this.cancelCombatStateSync()
  },
}
</script>

<style scoped>
.combat-main-row {
  align-items: flex-start;
}

.combat-left-column {
  display: flex;
  flex-direction: column;
  padding-right: 0;
}

.combat-hit-locations-column {
  display: flex;
  flex-direction: column;
  padding-left: 0;
  align-self: flex-start;
  transform: translateX(1.5rem);
}

.summary-column {
  position: relative;
  padding-right: 0;
}

.combat-summary-root {
  display: block;
  width: 100%;
  max-width: 100%;
}

.combat-summary-grid {
  width: 100%;
  margin-left: 0;
  margin-right: 0;
}

.combat-summary-section {
  flex: 0 0 auto;
  min-width: 0;
}

.combat-summary-section-chars {
  width: 37.288%;
}

.combat-summary-section-abilities {
  width: 62.712%;
}

.combat-left-footer-row {
  width: 94%;
  margin-left: 0;
  margin-right: 0;
}

.power-source-overflow-message {
  color: orange;
}

.favourites-tooltip-trigger.bb-v-tooltip::after {
  min-width: 280px;
  max-width: 520px;
  min-height: 120px;
  text-align: left;
}

@media (min-width: 768px) {
  .combat-left-column {
    flex: 0 0 46%;
    max-width: 46%;
  }

  .combat-hit-locations-column {
    flex: 0 0 54%;
    max-width: 54%;
  }

  .combat-summary-column {
    flex: 0 0 27.333333%;
    max-width: 27.333333%;
  }
}
</style>
